import type { Notification } from '@/types';

export type ChatNotificationSource = 'channel' | 'direct' | 'group';

export interface ChatNotificationContext {
  senderName: string;
  senderAvatar?: string;
  source: ChatNotificationSource;
  sourceLabel: string;
  preview: string;
  mentioned: boolean;
}

function metadataString(notification: Notification, key: string): string | undefined {
  const value = notification.metadata?.[key];
  return typeof value === 'string' && value.trim() ? value.trim() : undefined;
}

function legacyChannelTitle(notification: Notification): {
  senderName?: string;
  channelName?: string;
} {
  if (notification.type !== 'CHANNEL_MESSAGE') return {};
  const separator = notification.title.lastIndexOf(' in ');
  if (separator <= 0) return {};
  return {
    senderName: notification.title.slice(0, separator).trim(),
    channelName: notification.title
      .slice(separator + 4)
      .trim()
      .replace(/^#/, ''),
  };
}

function readableMessagePreview(message: string): string {
  const value = message.trim();
  if (!value.startsWith('[POLL:')) return value;

  try {
    const poll = JSON.parse(value.slice(6, -1)) as { question?: unknown };
    if (typeof poll.question === 'string' && poll.question.trim()) {
      return `Poll: ${poll.question.trim()}`;
    }
  } catch {
    // Older records can contain a preview truncated before the JSON ends.
    const questionMatch = value.match(/"question":"((?:\\.|[^"\\])*)"/);
    if (questionMatch?.[1]) {
      try {
        return `Poll: ${JSON.parse(`"${questionMatch[1]}"`) as string}`;
      } catch {
        // Fall through to the safe label below.
      }
    }
  }
  return 'Shared a poll';
}

/**
 * Normalizes both current structured chat notifications and older records that
 * only stored "Sender in Channel" in the title.
 */
export function getChatNotificationContext(
  notification: Notification
): ChatNotificationContext | null {
  const isChatNotification =
    notification.type === 'CHANNEL_MESSAGE' ||
    notification.type === 'DIRECT_MESSAGE' ||
    notification.metadata?.source === 'channel_message';

  if (!isChatNotification) return null;

  const legacy = legacyChannelTitle(notification);
  const channelKind = metadataString(notification, 'channelKind');
  const isDirect = notification.type === 'DIRECT_MESSAGE' || notification.metadata?.direct === true;
  const source: ChatNotificationSource =
    channelKind === 'group_dm' ? 'group' : isDirect ? 'direct' : 'channel';
  const senderName =
    metadataString(notification, 'senderName') ||
    notification.actor?.name?.trim() ||
    legacy.senderName ||
    notification.title ||
    'A teammate';
  const channelName = metadataString(notification, 'channelName') || legacy.channelName;
  const storedDestination = metadataString(notification, 'destinationLabel');
  const normalizedChannelName = (channelName || storedDestination || '').replace(/^#/, '');
  const sourceLabel =
    source === 'direct'
      ? 'Direct message'
      : source === 'group'
        ? channelName || storedDestination || 'Group message'
        : normalizedChannelName || 'Channel';

  return {
    senderName,
    senderAvatar: metadataString(notification, 'senderAvatar'),
    source,
    sourceLabel,
    preview: readableMessagePreview(notification.message || ''),
    mentioned: notification.metadata?.mentioned === true,
  };
}
